import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import {
  MdArrowBackIosNew,
  MdOutlineAddPhotoAlternate,
  MdOutlineClose,
  MdSend,
} from 'react-icons/md';
import Swal from 'sweetalert2';

import useMessages from '@/lib/hooks/useMessages';

import { messageIsRead } from './helpers';
import styles from './index.module.scss';
import Message, { type MessageProps } from './Message';

const FeedbackMessageModal: FC<{
  onClose: () => void;
  feedbackId: string;
  title: string;
  user: { id: string; name: string };
}> = ({ onClose, feedbackId, title, user }) => {
  const [messages, setMessages] = useState<MessageProps[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [currentUploadImage, setCurrentUploadImage] = useState<File | null>(
    null
  );

  const {
    messages: rawMessages,
    sendMessage,
    fetchMore,
  } = useMessages('feedback', feedbackId);

  const messagesRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (rawMessages) {
      setMessages(
        rawMessages.map((message) => ({
          id: message._id,
          type: message.sender.id === user.id ? 'send' : 'receive',
          content: message.content,
          createdAt: message.createdTime,
          name: message.sender.name,
        }))
      );
      messageIsRead({
        associatedEntity: 'feedback',
        entityId: feedbackId,
        senderId: user.id,
      });
    }
  }, [feedbackId, rawMessages, user.id]);

  const uploadPreviewUrl =
    currentUploadImage && URL.createObjectURL(currentUploadImage);

  const handleCreateMessage = async () => {
    try {
      if (currentUploadImage) {
        await sendMessage(currentUploadImage);
        setCurrentUploadImage(null);
        if (imageInputRef.current) {
          imageInputRef.current.value = '';
        }
      }
      if (newMessage) {
        await sendMessage(newMessage);
        setNewMessage('');
      }
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: '新增訊息失敗',
        text: '請稍後再試',
      });
    }
  };

  return (
    <div className={styles.feedbackMessageModal}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onClose}>
          <MdArrowBackIosNew />
        </button>
        <h2 className={styles.title}>{title}</h2>
      </div>
      <div
        className={styles.messages}
        ref={messagesRef}
        onScroll={(e) => {
          const nativeTarget = e.nativeEvent.target;
          if (nativeTarget instanceof HTMLDivElement) {
            const { scrollTop, scrollHeight, offsetHeight } = nativeTarget;
            const scrollDistance = scrollTop > 0 ? scrollTop : -scrollTop;
            if (scrollHeight - offsetHeight - scrollDistance < 100) {
              fetchMore();
            }
          }
        }}
      >
        {messages?.map((message) => (
          <Message key={message.id} {...message} />
        ))}
      </div>
      <div className={styles.newMessage}>
        <div className={styles.inputs}>
          <label htmlFor="imageInput" className={styles.imgInputLabel}>
            <input
              id="imageInput"
              type="file"
              multiple={false}
              accept="image/*"
              hidden
              ref={imageInputRef}
              onChange={(e) => {
                if (e.target.files) {
                  const file = e.target.files[0];
                  if (file.size > 1024 * 1024 * 10) {
                    e.target.value = '';
                    Swal.fire({
                      icon: 'error',
                      title: '上傳失敗',
                      text: '檔案大小超過 10MB',
                    });
                    return;
                  }
                  setCurrentUploadImage(file);
                }
              }}
            />
            <MdOutlineAddPhotoAlternate />
          </label>
          <input
            className={styles.textInput}
            placeholder="請輸入訊息"
            type="text"
            name="message"
            value={newMessage}
            ref={inputRef}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={!!uploadPreviewUrl}
          />
          <button className={styles.sendButton} onClick={handleCreateMessage}>
            <MdSend />
          </button>
        </div>
        {uploadPreviewUrl && (
          <div className={styles.display}>
            <img src={uploadPreviewUrl} alt="upload image preview" />
            <button
              className={styles.removeButton}
              onClick={() => {
                setCurrentUploadImage(null);
                if (imageInputRef.current) {
                  imageInputRef.current.value = '';
                }
              }}
            >
              <MdOutlineClose />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackMessageModal;
